import { isApiError } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Icon, StatusBadge, cn, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { IntegrationOut } from "../../shared";
import { savePendingConnect } from "../google-callback/pending-connect";
import { calendarStateOf, isGoogleAuthorizeUrl, useCalendarMutations } from "../use-google-calendar";
import { CalendarPicker } from "./calendar-picker";
import { Gate, useCanManage } from "./panel-kit";

const P = "settings.integrations.google";
const SERVICE_UNAVAILABLE = 503;

function DisconnectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { disconnect } = useCalendarMutations();
  const confirm = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        toast.info(t(`${P}.disconnected`));
        onOpenChange(false);
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t(`${P}.disconnectTitle`)}
        description={t(`${P}.disconnectBody`, { product: PRODUCT_NAME })}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => { onOpenChange(false); }}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={disconnect.isPending} onClick={confirm}>
              <Icon name="x-circle" />
              {t(`${P}.disconnectConfirm`)}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}

interface GoogleCalendarCardProps {
  integration: IntegrationOut;
  /** Same-tab redirect (rule 8: no auth popups). Injected for tests. */
  redirect?: (url: string) => void;
}

/** Google Calendar connect card: unavailable → Connect → connected (calendar picker, Meet, Disconnect). */
export function GoogleCalendarCard({ integration, redirect = (url) => { window.location.assign(url); } }: GoogleCalendarCardProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const manage = useCanManage("integrations.manage");
  const state = calendarStateOf(integration);
  const { connect } = useCalendarMutations();
  const [disconnecting, setDisconnecting] = useState(false);
  const start = () => {
    connect.mutate(undefined, {
      onSuccess: ({ authorize_url }) => {
        if (!isGoogleAuthorizeUrl(authorize_url)) {
          toast.error(t(`${P}.notConfigured`));
          return;
        }
        savePendingConnect({ wsSlug: summary.slug, wsId: summary.id });
        redirect(authorize_url);
      },
      onError: (error) => { toast.error(isApiError(error) && error.status === SERVICE_UNAVAILABLE ? t(`${P}.notConfigured`) : message(error)); },
    });
  };
  const redirecting = connect.isPending || connect.isSuccess;
  const reason = !state.connected && !state.available ? t(`${P}.unavailable`) : manage.reason;
  return (
    <article className={cn("card card-body", state.connected && "md:col-span-2")} data-testid="integration-google_calendar" aria-labelledby="gcal-title">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-muted">
          <Icon name="calendar" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="gcal-title" className="font-bold">{t(`${P}.name`)}</h3>
            {state.connected ? <StatusBadge status="success" label={t("settings.integrations.statusConnected")} /> : null}
            {!state.connected && state.available ? <Badge>{t("settings.integrations.statusDisconnected")}</Badge> : null}
            {!state.connected && !state.available ? <Badge><Icon name="clock" className="size-3" />{t("settings.integrations.statusUnavailable")}</Badge> : null}
          </div>
          <p className="mt-0.5 text-xs text-fg-muted">{state.connected ? t(`${P}.connectedAs`, { email: state.accountEmail ?? state.calendarName ?? "—" }) : t(`${P}.description`)}</p>
          {state.available ? null : <p className="mt-2 text-xs font-semibold text-fg-muted">{t(`${P}.unavailable`)}</p>}
        </div>
        {state.connected ? (
          <Gate reason={reason}>
            <Button variant="secondary" size="sm" disabled={!manage.allowed} onClick={() => { setDisconnecting(true); }}>
              {t("settings.integrations.disconnect")}
            </Button>
          </Gate>
        ) : null}
      </div>
      {state.connected ? null : (
        <div className="mt-3 pl-13">
          <Gate reason={reason}>
            <Button size="sm" disabled={!manage.allowed || !state.available} loading={redirecting} onClick={start}>
              <Icon name="log-in" />
              {redirecting ? t(`${P}.redirecting`) : t(`${P}.connect`)}
            </Button>
          </Gate>
        </div>
      )}
      {!state.connected && state.available ? <p className="mt-2 pl-13 text-xs text-fg-subtle">{t(`${P}.connectNote`, { product: PRODUCT_NAME })}</p> : null}
      {state.connected ? <CalendarPicker key={`${state.calendarId ?? ""}:${state.createMeet}`} state={state} canManage={manage.allowed} /> : null}
      <DisconnectDialog open={disconnecting} onOpenChange={setDisconnecting} />
    </article>
  );
}
